import { useEffect, useRef, useState } from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import api, {
    checkIntro,
    completeInitialCredentials,
    getDailyStatus,
    getPostCommentStatus,
    getPostLikeStatus,
    getPosts,
    getSpotlightStatus,
    updateIntro,
    uploadFile,
} from '@/lib/api';
import { PostCard } from '@/components/PostCard';
import { CreatePost } from '@/components/CreatePost';
import { Avatar } from '@/components/Avatar';
import { FloorTheme } from '@/lib/themes';
import { Download, Loader2, RefreshCw } from 'lucide-react';
import { AvatarEditor } from '@/components/AvatarEditor';
import { Button } from '@/components/ui/button';
import { TatemaeModal } from '@/components/TatemaeModal';
import { requestPushSubscription } from '@/lib/push';
import { getImageFileFromClipboard, insertTextAtCursor } from '@/utils/pasteImage';
import { autoResizeTextarea } from '@/utils/autoResize';
import { useI18n } from '@/lib/i18n';

type DailyStatus = {
    posted_today: boolean;
    post_count_today: number;
    daily_post_limit: number | null;
    remaining_posts_today: number | null;
    seconds_until_reset: number;
};

export function Home() {
    const [user, setUser] = useState<any>(null);
    const { t } = useI18n();
    const [posts, setPosts] = useState<any>([]);
    const [feedMode, setFeedMode] = useState<'all' | 'mine' | 'friends' | 'random' | 'latest' | 'trending'>('latest');
    const [isLoadingPosts, setIsLoadingPosts] = useState(false);
    const [refreshingFeed, setRefreshingFeed] = useState(false);
    const [isError, setIsError] = useState(false);
    const [introText, setIntroText] = useState('');
    const introTextRef = useRef<HTMLTextAreaElement>(null);
    const [introChecking, setIntroChecking] = useState(false);
    const [introSuggestion, setIntroSuggestion] = useState<any>(null);
    const [showIntroModal, setShowIntroModal] = useState(false);
    const [introError, setIntroError] = useState('');
    const [introSuccess, setIntroSuccess] = useState('');
    const [dailyStatus, setDailyStatus] = useState<DailyStatus | null>(null);
    const [timeLeft, setTimeLeft] = useState<number | null>(null);
    const [spotlightStatus, setSpotlightStatus] = useState<{ spotlight_post_id?: number | null; self_spotlight_post_id?: number | null } | null>(null);
    const [showCredentialModal, setShowCredentialModal] = useState(false);
    const [newEmail, setNewEmail] = useState('');
    const [confirmEmail, setConfirmEmail] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [credentialError, setCredentialError] = useState('');
    const [credentialSubmitting, setCredentialSubmitting] = useState(false);
    const [showInstallGuide, setShowInstallGuide] = useState(false);
    const [showNotifyGuide, setShowNotifyGuide] = useState(false);
    const [isIosDevice, setIsIosDevice] = useState(false);
    const [isStandalone, setIsStandalone] = useState(false);
    const [pushPermission, setPushPermission] = useState<NotificationPermission | 'unsupported'>('default');
    const [iosInstallHint, setIosInstallHint] = useState('');
    const initialFeedLoadRef = useRef(false);
    const postsRequestRef = useRef(0);
    const [pushNotifyHint, setPushNotifyHint] = useState('');
    const [isPushBusy, setIsPushBusy] = useState(false);
    // Avatar Upload State
    const [selectedImage, setSelectedImage] = useState<string | null>(null);

    // Use optional chaining or casting for context to avoid crashes
    const context = useOutletContext<{ theme: FloorTheme; user: any }>();
    const theme = context?.theme || {
        name: 'Loading...',
        description: '',
        colors: { primary: '#000', secondary: '#fff', accent: '#000', background: '#fff', text: '#000', card: '#fff', border: '#000' },
        vibe: ''
    };

    useEffect(() => {
        if (!context?.user) return;
        setUser(context.user);
        setIntroText(context.user.intro_text || '');
    }, [context?.user]);

    useEffect(() => {
        if (typeof window === 'undefined') return;

        const ua = navigator.userAgent || '';
        const ios = /iPad|iPhone|iPod/i.test(ua);
        setIsIosDevice(ios);

        const updateStandalone = () => {
            const standalone = window.matchMedia('(display-mode: standalone)').matches;
            const legacyStandalone = (navigator as Navigator & { standalone?: boolean }).standalone === true;
            setIsStandalone(standalone || legacyStandalone);
        };
        updateStandalone();

        const updatePermission = () => {
            if (typeof Notification === 'undefined') {
                setPushPermission('unsupported');
                return;
            }
            setPushPermission(Notification.permission);
        };
        updatePermission();

        const mediaQuery = window.matchMedia('(display-mode: standalone)');
        const handleDisplayChange = () => updateStandalone();
        if (mediaQuery.addEventListener) {
            mediaQuery.addEventListener('change', handleDisplayChange);
        } else {
            mediaQuery.addListener(handleDisplayChange);
        }

        const handleVisibility = () => updatePermission();
        document.addEventListener('visibilitychange', handleVisibility);

        return () => {
            if (mediaQuery.removeEventListener) {
                mediaQuery.removeEventListener('change', handleDisplayChange);
            } else {
                mediaQuery.removeListener(handleDisplayChange);
            }
            document.removeEventListener('visibilitychange', handleVisibility);
        };
    }, []);

    const pickTextColor = (color: string) => {
        const rgbMatch = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
        if (rgbMatch) {
            const r = Number(rgbMatch[1]);
            const g = Number(rgbMatch[2]);
            const b = Number(rgbMatch[3]);
            const luminance = (0.299 * r + 0.587 * g + 0.114 * b);
            return luminance > 160 ? '#111111' : '#ffffff';
        }

        const hex = color.replace('#', '');
        if (hex.length === 3 || hex.length === 6) {
            const full = hex.length === 3
                ? hex.split('').map((c) => c + c).join('')
                : hex;
            const r = parseInt(full.slice(0, 2), 16);
            const g = parseInt(full.slice(2, 4), 16);
            const b = parseInt(full.slice(4, 6), 16);
            const luminance = (0.299 * r + 0.587 * g + 0.114 * b);
            return luminance > 160 ? '#111111' : '#ffffff';
        }

        return '#111111';
    };

    const introCountToday = (() => {
        if (!user) return 0;
        const today = new Date().toISOString().split('T')[0];
        if (user.intro_update_date !== today) return 0;
        return Number(user.intro_update_count || 0);
    })();
    const introRemaining = Math.max(0, 5 - introCountToday);

    const iosInstallStatusLabel = !isIosDevice ? t('iOS端末で確認') : (isStandalone ? t('追加済み') : t('未追加'));
    const pushPermissionLabel = (() => {
        if (pushPermission === 'unsupported') return t('未対応');
        if (pushPermission === 'granted') return t('許可済み');
        if (pushPermission === 'denied') return t('拒否中');
        return t('未許可');
    })();

    const handleOpenIosShare = async () => {
        setIosInstallHint('');
        if (!isIosDevice) {
            setIosInstallHint(t('iOS端末で開くと共有メニューが使えます。'));
            return;
        }
        if (typeof navigator === 'undefined') return;
        if (!('share' in navigator)) {
            setIosInstallHint(t('共有ボタン「□↑」をタップして「ホーム画面に追加」を選んでください。'));
            return;
        }
        try {
            await navigator.share({ title: document.title, url: window.location.href });
            setIosInstallHint(t('共有メニューを開きました。'));
        } catch (err) {
            setIosInstallHint(t('共有メニューを開けませんでした。画面下の共有ボタン「□↑」から追加してください。'));
        }
    };

    const handleEnablePush = async () => {
        setPushNotifyHint('');
        if (typeof Notification === 'undefined') {
            setPushNotifyHint(t('この端末・ブラウザでは通知に未対応です。'));
            setPushPermission('unsupported');
            return;
        }
        if (isIosDevice && !isStandalone) {
            setPushNotifyHint(t('通知はホーム画面に追加したアプリから起動した時だけ有効です。'));
            return;
        }
        setIsPushBusy(true);
        const result = await requestPushSubscription();
        setIsPushBusy(false);

        if (result.ok) {
            setPushNotifyHint(t('通知を許可しました。'));
        } else if (result.reason === 'permission') {
            setPushNotifyHint(t('通知が許可されませんでした。設定から許可してください。'));
        } else if (result.reason === 'unsupported') {
            setPushNotifyHint(t('この端末・ブラウザでは通知に未対応です。'));
        } else if (result.reason === 'missing_key') {
            setPushNotifyHint(t('通知の設定がまだ準備中です。'));
        } else if (result.reason === 'insecure') {
            setPushNotifyHint(t('安全な接続でアクセスしてください。'));
        } else {
            setPushNotifyHint(t('通知の設定に失敗しました。時間を置いて再度お試しください。'));
        }

        if (typeof Notification === 'undefined') {
            setPushPermission('unsupported');
        } else {
            setPushPermission(Notification.permission);
        }
    };

    useEffect(() => {
        if (!user?.must_change_credentials) {
            setShowCredentialModal(false);
            setCredentialError('');
            return;
        }
        setShowCredentialModal(true);
    }, [user?.must_change_credentials]);

    const refreshPosts = async (mode = feedMode) => {
        const requestId = ++postsRequestRef.current;
        setIsLoadingPosts(true);
        try {
            const postsRes = await getPosts(mode);
            const basePosts = postsRes.data || [];
            if (requestId !== postsRequestRef.current) return;
            if (basePosts.length === 0) {
                setPosts([]);
                return;
            }
            const ids = basePosts.map((post: any) => post.post_id).filter(Boolean);
            const [statusRes, likeStatusRes] = await Promise.all([
                getPostCommentStatus(ids).catch((err) => {
                    console.error('Failed to fetch comment status', err);
                    return { data: [] as any[] };
                }),
                getPostLikeStatus(ids).catch((err) => {
                    console.error('Failed to fetch like status', err);
                    return { data: [] as any[] };
                }),
            ]);
            if (requestId !== postsRequestRef.current) return;
            const statusMap = new Map<number, string | null>();
            (statusRes.data || []).forEach((item: any) => {
                statusMap.set(item.post_id, item.last_comment_at || null);
            });
            const likesMap = new Map<number, any[]>();
            (likeStatusRes.data || []).forEach((item: any) => {
                likesMap.set(item.post_id, item.likes || []);
            });
            setPosts(basePosts.map((post: any) => ({
                ...post,
                last_comment_at: statusMap.get(post.post_id) || null,
                ...(likesMap.has(post.post_id)
                    ? { like_entries: likesMap.get(post.post_id) || [] }
                    : {}),
            })));
        } catch (err) {
            console.error("Failed to fetch posts", err);
            if (requestId === postsRequestRef.current) setIsError(true);
        } finally {
            if (requestId === postsRequestRef.current) setIsLoadingPosts(false);
        }
    };

    const handleRefreshFeed = async () => {
        if (refreshingFeed) return;
        setRefreshingFeed(true);
        try {
            await Promise.all([
                refreshPosts(feedMode),
                refreshSpotlightStatus(),
                refreshUserStats(),
                refreshDailyStatus(),
            ]);
        } finally {
            setRefreshingFeed(false);
        }
    };

    useEffect(() => {
        if (!user?.user_id) return;
        if (!initialFeedLoadRef.current) {
            initialFeedLoadRef.current = true;
            handleRefreshFeed();
            return;
        }
        refreshPosts(feedMode);
    }, [feedMode, user?.user_id]);

    useEffect(() => {
        if (!dailyStatus) {
            setTimeLeft(null);
            return;
        }
        setTimeLeft(dailyStatus.seconds_until_reset);
        const timer = setInterval(() => {
            setTimeLeft((prev) => (prev !== null ? Math.max(0, prev - 1) : prev));
        }, 1000);
        return () => clearInterval(timer);
    }, [dailyStatus?.seconds_until_reset]);

    const refreshUserStats = async () => {
        try {
            const meRes = await api.get('/users/me');
            const userData = meRes.data;
            setUser(userData);
            localStorage.setItem('user', JSON.stringify(userData));
            setIntroText(userData.intro_text || '');
            window.dispatchEvent(new CustomEvent('maison:user-updated', { detail: userData }));
        } catch (err) {
            console.error("Failed to refresh user", err);
        }
    };

    const refreshDailyStatus = async () => {
        try {
            const res = await getDailyStatus();
            const nextStatus: DailyStatus = {
                posted_today: Boolean(res.data?.posted_today),
                post_count_today: Number(res.data?.post_count_today ?? 0),
                daily_post_limit: res.data?.daily_post_limit === null || res.data?.daily_post_limit === undefined
                    ? null
                    : Number(res.data.daily_post_limit),
                remaining_posts_today: res.data?.remaining_posts_today === null || res.data?.remaining_posts_today === undefined
                    ? null
                    : Number(res.data.remaining_posts_today),
                seconds_until_reset: Number(res.data?.seconds_until_reset ?? 0),
            };
            setDailyStatus(nextStatus);
            setTimeLeft(nextStatus.seconds_until_reset);
        } catch (err) {
            console.error('Failed to fetch daily status', err);
        }
    };

    const refreshSpotlightStatus = async () => {
        try {
            const res = await getSpotlightStatus();
            setSpotlightStatus(res.data);
        } catch (err) {
            console.error('Failed to fetch spotlight status', err);
        }
    };

    const blockCredentialClipboard = (
        event: React.ClipboardEvent<HTMLInputElement> | React.DragEvent<HTMLInputElement>
    ) => {
        event.preventDefault();
        setCredentialError('この画面ではコピー＆ペーストは使用できません。手入力してください。');
    };

    const handleCredentialSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!user?.must_change_credentials) return;

        const needsEmailUpdate = !Boolean(user?.email_changed_once);
        const needsPasswordUpdate = !Boolean(user?.password_changed_once);
        const normalizedEmail = newEmail.trim().toLowerCase();
        const normalizedConfirmEmail = confirmEmail.trim().toLowerCase();
        const normalizedPassword = newPassword.trim();
        const normalizedConfirmPassword = confirmPassword.trim();

        if (!needsEmailUpdate && !needsPasswordUpdate) {
            setCredentialError('更新対象の認証情報がありません。');
            return;
        }

        if (needsEmailUpdate) {
            if (!normalizedEmail || !normalizedConfirmEmail) {
                setCredentialError('メールアドレスを2回入力してください。');
                return;
            }
            if (normalizedEmail !== normalizedConfirmEmail) {
                setCredentialError('メールアドレスが一致しません。');
                return;
            }
        }

        if (needsPasswordUpdate) {
            if (!normalizedPassword || !normalizedConfirmPassword) {
                setCredentialError('パスワードを2回入力してください。');
                return;
            }
            if (normalizedPassword !== normalizedConfirmPassword) {
                setCredentialError('パスワードが一致しません。');
                return;
            }
            if (normalizedPassword.length < 8) {
                setCredentialError('パスワードは8文字以上で入力してください。');
                return;
            }
        }

        setCredentialSubmitting(true);
        setCredentialError('');
        try {
            const payload: {
                new_email?: string;
                confirm_email?: string;
                new_password?: string;
                confirm_password?: string;
            } = {};
            if (needsEmailUpdate) {
                payload.new_email = normalizedEmail;
                payload.confirm_email = normalizedConfirmEmail;
            }
            if (needsPasswordUpdate) {
                payload.new_password = normalizedPassword;
                payload.confirm_password = normalizedConfirmPassword;
            }
            const res = await completeInitialCredentials(payload);
            const nextUser = res.data;
            setUser(nextUser);
            localStorage.setItem('user', JSON.stringify(nextUser));
            window.dispatchEvent(new Event('maison:user-updated'));
            setShowCredentialModal(false);
            setNewEmail('');
            setConfirmEmail('');
            setNewPassword('');
            setConfirmPassword('');
        } catch (err: any) {
            const detail = err?.response?.data?.detail;
            if (typeof detail === 'string' && detail.trim()) {
                setCredentialError(detail);
            } else if (detail?.message) {
                setCredentialError(detail.message);
            } else {
                setCredentialError('メールアドレスとパスワードの更新に失敗しました。');
            }
        } finally {
            setCredentialSubmitting(false);
        }
    };

    const handleSaveAvatar = async (blob: Blob) => {
        const formData = new FormData();
        formData.append('file', blob, 'avatar.jpg');
        try {
            await api.post('/users/me/avatar', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            // Refresh user data
            const meRes = await api.get('/users/me');
            setUser(meRes.data);
        } catch (err) {
            console.error('Failed to upload avatar', err);
            alert('画像のアップロードに失敗しました。');
        } finally {
            setSelectedImage(null);
        }
    };

    const handleIntroSubmit = async () => {
        const content = introText.trim();
        if (!content) {
            setIntroError('自己紹介文を入力してください。');
            return;
        }
        setIntroChecking(true);
        setIntroError('');
        setIntroSuccess('');
        try {
            const checkRes = await checkIntro(content);
            const data = checkRes.data;
            if (data?.error) {
                setIntroError(`AI校正に失敗しました: ${data.error}`);
                return;
            }
            if (data.is_rewritten) {
                setIntroSuggestion(data);
                setShowIntroModal(true);
                return;
            }
            await updateIntro(content);
            setIntroSuccess('自己紹介文を更新しました。');
            await refreshUserStats();
        } catch (err: any) {
            if (err?.response?.status === 429) {
                setIntroError('自己紹介の更新は1日5回までです。');
            } else {
                setIntroError('自己紹介の更新に失敗しました。');
            }
        } finally {
            setIntroChecking(false);
        }
    };

    const handleIntroPaste = async (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
        const imageFile = getImageFileFromClipboard(e);
        if (!imageFile) return;
        e.preventDefault();
        if (!imageFile.type.startsWith('image/')) return;
        try {
            const res = await uploadFile(imageFile);
            const url = res.data?.url;
            if (url) {
                insertTextAtCursor(e.currentTarget, introText, `\n${url}\n`, setIntroText);
            }
        } catch (err) {
            console.error('Paste upload failed', err);
            setIntroError('画像の貼り付けに失敗しました。');
        }
    };

    useEffect(() => {
        autoResizeTextarea(introTextRef.current);
    }, [introText]);

    const handleIntroAccept = async () => {
        if (!introSuggestion?.converted) return;
        setIntroChecking(true);
        setIntroError('');
        try {
            await updateIntro(introSuggestion.converted);
            setIntroText(introSuggestion.converted);
            setIntroSuccess('自己紹介文を更新しました。');
            setShowIntroModal(false);
            setIntroSuggestion(null);
            await refreshUserStats();
        } catch (err: any) {
            if (err?.response?.status === 429) {
                setIntroError('自己紹介の更新は1日5回までです。');
            } else {
                setIntroError('自己紹介の更新に失敗しました。');
            }
        } finally {
            setIntroChecking(false);
        }
    };

    if (isError) {
        return (
            <div className="flex h-screen items-center justify-center flex-col gap-4">
                <p className="text-red-500">Failed to load user data.</p>
                <button
                    onClick={() => window.location.reload()}
                    className="px-4 py-2 bg-black text-white rounded hover:bg-gray-800"
                >
                    Reload
                </button>
            </div>
        );
    }

    if (!user) return <div className="flex h-screen items-center justify-center"><Loader2 className="animate-spin" /></div>;

    const formatCountdown = (seconds: number | null) => {
        if (seconds === null) return '--:--:--';
        const hrs = Math.floor(seconds / 3600);
        const mins = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;
        return `${String(hrs).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    };

    const showDailyNotice = !['Admin', 'Maestro', 'Manager'].includes(user.role_type);
    const dailyPostProgress = (() => {
        if (!dailyStatus) return '-- / --';
        const limitLabel = dailyStatus.daily_post_limit === null ? '∞' : String(dailyStatus.daily_post_limit);
        return `${dailyStatus.post_count_today} / ${limitLabel}`;
    })();
    const needsEmailCredentialUpdate = Boolean(user?.must_change_credentials && !user?.email_changed_once);
    const needsPasswordCredentialUpdate = Boolean(user?.must_change_credentials && !user?.password_changed_once);
    const credentialTargetLabel = [needsEmailCredentialUpdate ? 'メールアドレス' : null, needsPasswordCredentialUpdate ? 'パスワード' : null]
        .filter(Boolean)
        .join('・');

    return (
        <div className="max-w-7xl mx-auto space-y-8">
            {/* Welcome Banner */}
            {/* Welcome Banner */}
            <header
                className="backdrop-blur rounded-xl p-8 shadow-sm border transition-all duration-500 relative overflow-hidden"
                style={{
                    backgroundColor: `${theme.colors.card}E6`, // ~90% opacity
                    borderColor: theme.colors.border,
                    color: theme.colors.text
                }}
            >


                <div className="relative z-10 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                    <div>
                        <h2 className="text-2xl font-bold mb-1 drop-shadow-md">Welcome back, {user.username}</h2>
                        <div className="font-bold mb-1 drop-shadow-md text-lg" style={{ color: theme.colors.primary }}>
                            {!['Admin', 'Maestro', 'Manager'].includes(user.role_type) ? `Floor${user.floor_level} ` : ''}
                            「{theme.name}」
                        </div>
                        <p className="opacity-90 text-sm font-medium drop-shadow-sm">{theme.description}</p>
                        {dailyStatus && (
                            <div className="mt-3 flex max-w-full flex-wrap items-center gap-x-3 gap-y-2 text-xs sm:text-sm">
                                <span
                                    className="rounded-full border px-3 py-1 font-semibold"
                                    style={{
                                        borderColor: theme.colors.primary,
                                        color: theme.colors.primary,
                                        backgroundColor: `${theme.colors.card}D9`,
                                    }}
                                >
                                    本日の投稿数 {dailyPostProgress}
                                </span>
                                <span
                                    className="rounded-full border px-3 py-1 font-semibold"
                                    style={{
                                        borderColor: theme.colors.border,
                                        color: theme.colors.text,
                                        backgroundColor: `${theme.colors.card}D9`,
                                    }}
                                >
                                    リセットまで {formatCountdown(timeLeft)}
                                </span>
                            </div>
                        )}
                        {showDailyNotice && dailyStatus && !dailyStatus.posted_today && timeLeft !== null && (
                            <div className="mt-3 text-sm font-semibold" style={{ color: theme.colors.primary }}>
                                本日の投稿がまだです。残り時間 {formatCountdown(timeLeft)}。間に合わなかった場合、TokuScoreが10点減点されます。
                            </div>
                        )}
                    </div>

                    <div className="flex flex-wrap gap-4 justify-start md:justify-end">
                        <a
                            href="/downloads/maison-portal-native.apk"
                            download
                            className="px-5 py-2 rounded-full font-semibold border shadow-sm flex items-center justify-center gap-2 min-w-[150px] transition hover:opacity-85"
                            style={{
                                backgroundColor: theme.colors.card,
                                borderColor: theme.colors.primary,
                                color: theme.colors.primary,
                            }}
                        >
                            <Download size={16} />
                            Android版
                        </a>
                        <a
                            href="/downloads/maison-portal-native.ipa"
                            download
                            className="px-5 py-2 rounded-full font-semibold border shadow-sm flex items-center justify-center gap-2 min-w-[150px] transition hover:opacity-85"
                            style={{
                                backgroundColor: theme.colors.card,
                                borderColor: theme.colors.primary,
                                color: theme.colors.primary,
                            }}
                        >
                            <Download size={16} />
                            iPhone／iPad版
                        </a>
                        {['Admin', 'Maestro', 'Manager'].includes(user.role_type) ? (
                            <>
                                <div
                                    className="px-6 py-2 rounded-full font-medium border shadow-sm flex flex-col items-center min-w-[100px]"
                                    style={{
                                        backgroundColor: theme.colors.card,
                                        borderColor: theme.colors.primary,
                                        color: theme.colors.primary
                                    }}
                                >
                                    <span className="text-xs uppercase opacity-70">Current Floor</span>
                                    <span className="text-xl font-bold font-serif">∞</span>
                                </div>
                                <div
                                    className="px-6 py-2 rounded-full font-medium border shadow-sm flex flex-col items-center min-w-[100px]"
                                    style={{
                                        backgroundColor: theme.colors.card,
                                        borderColor: theme.colors.primary,
                                        color: theme.colors.primary
                                    }}
                                >
                                    <span className="text-xs uppercase opacity-70">TOKU SCORE</span>
                                    <span className="text-xl font-bold font-serif">∞</span>
                                </div>
                            </>
                        ) : (
                            <>
                                <div
                                    className="px-6 py-2 rounded-full font-medium border shadow-sm flex flex-col items-center min-w-[100px]"
                                    style={{
                                        backgroundColor: theme.colors.card,
                                        borderColor: theme.colors.primary,
                                        color: theme.colors.primary
                                    }}
                                >
                                    <span className="text-xs uppercase opacity-70">Current Floor</span>
                                    <span className="text-xl font-bold font-serif">{user.floor_level}F</span>
                                </div>
                                <div
                                    className="px-6 py-2 rounded-full font-medium border shadow-sm flex flex-col items-center min-w-[100px]"
                                    style={{
                                        backgroundColor: theme.colors.card,
                                        borderColor: theme.colors.primary,
                                        color: theme.colors.primary
                                    }}
                                >
                                    <span className="text-xs uppercase opacity-70">TOKU SCORE</span>
                                    <span className="text-xl font-bold font-serif">{user.smile_score}</span>
                                </div>

                                {user.unito_enabled && (
                                    <>
                                        <div
                                            className="px-6 py-2 rounded-full font-medium border shadow-sm flex flex-col items-center min-w-[100px]"
                                            style={{
                                                backgroundColor: theme.colors.card,
                                                borderColor: theme.colors.accent,
                                                color: theme.colors.accent
                                            }}
                                        >
                                            <span className="text-xs uppercase opacity-70">UNITO CURRENT</span>
                                            <span className="text-xl font-bold font-serif">{user.unito_points}</span>
                                        </div>
                                        <div
                                            className="px-6 py-2 rounded-full font-medium border shadow-sm flex flex-col items-center min-w-[100px]"
                                            style={{
                                                backgroundColor: theme.colors.card,
                                                borderColor: theme.colors.accent,
                                                color: theme.colors.accent
                                            }}
                                        >
                                            <span className="text-xs uppercase opacity-70">UNITO TOTAL</span>
                                            <span className="text-xl font-bold font-serif">{user.unito_points_total ?? user.unito_points}</span>
                                        </div>
                                    </>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </header>

            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
                {/* Main Feed */}
                <div className="lg:col-span-3 space-y-6">
                    <div
                        className="p-6 rounded-xl shadow-sm border backdrop-blur"
                        style={{
                            backgroundColor: `${theme.colors.card}E6`,
                            borderColor: theme.colors.border,
                            color: theme.colors.text
                        }}
                    >
                        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                            <div>
                                <h3 className="font-semibold">{t('自己紹介')}</h3>
                                <p className="text-xs opacity-70 mt-1">{t('変更は1日5回まで。ポイントや投稿可能数には影響しません。')}</p>
                            </div>
                            <span className="text-xs opacity-70">{t('本日の残り')}: {introRemaining}/5</span>
                        </div>
                        <textarea
                            ref={introTextRef}
                            value={introText}
                            onChange={(e) => setIntroText(e.target.value)}
                            onInput={(e) => autoResizeTextarea(e.currentTarget)}
                            onPaste={handleIntroPaste}
                            rows={3}
                            className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 resize-none text-sm placeholder:opacity-50"
                            style={{
                                backgroundColor: theme.colors.background,
                                borderColor: theme.colors.border,
                                color: pickTextColor(theme.colors.background),
                            }}
                            placeholder={t('自己紹介文を入力してください...')}
                        />
                        <div className="flex justify-between items-center mt-3">
                            <div className="text-xs">
                                {introError && <span className="text-red-500">{introError}</span>}
                                {!introError && introSuccess && <span className="text-green-600">{introSuccess}</span>}
                            </div>
                            <Button
                                onClick={handleIntroSubmit}
                                disabled={introChecking}
                                className="gap-2"
                                style={{ backgroundColor: theme.colors.primary, color: pickTextColor(theme.colors.primary) }}
                            >
                                {introChecking ? t('検閲中...') : t('更新する')}
                            </Button>
                        </div>
                    </div>

                    <CreatePost
                        user={user}
                        onPostCreated={async () => {
                            await refreshPosts(feedMode);
                            await refreshUserStats();
                            await refreshDailyStatus();
                        }}
                    />

                    <TatemaeModal
                        isOpen={showIntroModal && !!introSuggestion}
                        data={introSuggestion}
                        onClose={() => {
                            setShowIntroModal(false);
                            setIntroSuggestion(null);
                        }}
                        onAccept={handleIntroAccept}
                        onSelfEdit={() => {
                            setShowIntroModal(false);
                            if (introSuggestion?.converted) {
                                setIntroText(introSuggestion.converted);
                            }
                            setIntroSuggestion(null);
                        }}
                    />

                    <div className="space-y-4">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                            <div className="flex items-center gap-2">
                                <div className="text-sm font-semibold" style={{ color: theme.colors.text }}>{t('タイムライン')}</div>
                                <button
                                    onClick={handleRefreshFeed}
                                    disabled={refreshingFeed}
                                    className="flex items-center gap-1 text-xs px-2 py-1 rounded-full border"
                                    style={{
                                        color: refreshingFeed ? 'rgba(148, 163, 184, 0.8)' : theme.colors.text,
                                        borderColor: theme.colors.border,
                                        backgroundColor: refreshingFeed ? 'transparent' : 'rgba(255,255,255,0.04)',
                                    }}
                                >
                                    <RefreshCw size={12} className={refreshingFeed ? 'animate-spin' : ''} />
                                    {t('更新')}
                                </button>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {[
                                    { key: 'all', label: t('すべて') },
                                    { key: 'mine', label: t('自分') },
                                    { key: 'friends', label: t('隣人') },
                                    { key: 'random', label: t('ランダム') },
                                    { key: 'latest', label: t('最新(1時間)') },
                                    { key: 'trending', label: t('トレンド') }
                                ].map((item) => {
                                    const isActive = feedMode === item.key;
                                    return (
                                        <button
                                            key={item.key}
                                            onClick={() => setFeedMode(item.key as typeof feedMode)}
                                            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                                                isActive ? 'shadow-sm' : 'hover:shadow-sm'
                                            }`}
                                            style={{
                                                borderColor: isActive ? theme.colors.primary : theme.colors.border,
                                                backgroundColor: isActive ? theme.colors.primary : 'rgba(255,255,255,0.7)',
                                                color: isActive ? pickTextColor(theme.colors.primary) : theme.colors.text
                                            }}
                                        >
                                            {item.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="space-y-6">
                            {isLoadingPosts && posts.length > 0 && (
                                <div className="flex items-center gap-2 text-xs opacity-60" aria-live="polite">
                                    <Loader2 className="animate-spin" size={14} />
                                    {t('更新中...')}
                                </div>
                            )}
                            {isLoadingPosts && posts.length === 0 ? (
                                <div className="flex items-center gap-2 text-sm opacity-70">
                                    <Loader2 className="animate-spin" size={16} />
                                    {t('読み込み中...')}
                                </div>
                            ) : posts.length > 0 ? (
                                posts.map((post: any) => (
                                    <PostCard
                                        key={post.post_id}
                                        post={post}
                                        currentUserId={user?.user_id}
                                        currentUser={user}
                                        spotlightPostId={spotlightStatus?.spotlight_post_id ?? null}
                                        selfSpotlightPostId={spotlightStatus?.self_spotlight_post_id ?? null}
                                        onSpotlightChange={refreshSpotlightStatus}
                                        onScoreChange={refreshUserStats}
                                        onDeleted={() => refreshPosts(feedMode)}
                                    />
                                ))
                            ) : (
                                <div className="text-sm opacity-70">
                                    {t('まだ投稿がありません。表示を切り替えてみてください。')}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Sidebar Stats / Info */}
                <div className="space-y-6 lg:col-span-2">
                    <div
                        className="p-6 rounded-xl shadow-sm border backdrop-blur"
                        style={{
                            backgroundColor: `${theme.colors.card}E6`,
                            borderColor: theme.colors.border,
                            color: theme.colors.text
                        }}
                    >
                        <h3 className="font-semibold mb-4 border-b pb-2" style={{ borderColor: theme.colors.border }}>
                            アプリ化・通知ガイド
                        </h3>
                        <div className="space-y-3">
                            <Button
                                onClick={() => setShowInstallGuide((prev) => !prev)}
                                className="w-full justify-between"
                                style={{ backgroundColor: theme.colors.primary, color: pickTextColor(theme.colors.primary) }}
                            >
                                <span>インストール方法（Android / iOS）</span>
                                <span className="text-xs">{showInstallGuide ? '閉じる' : '開く'}</span>
                            </Button>
                            {showInstallGuide && (
                                <div className="grid gap-3 sm:grid-cols-2 text-xs">
                                    <div className="rounded-lg border p-3 bg-white/70" style={{ borderColor: theme.colors.border }}>
                                        <div className="font-semibold mb-2">Android（Chrome）</div>
                                        <ol className="list-decimal pl-4 space-y-1">
                                            <li>SafariではなくChromeでサイトを開く</li>
                                            <li>右上「︙」→「アプリをインストール」または「ホーム画面に追加」</li>
                                            <li>確認して追加</li>
                                        </ol>
                                    </div>
                                    <div className="rounded-lg border p-3 bg-white/70" style={{ borderColor: theme.colors.border }}>
                                        <div className="font-semibold mb-2">iOS（Safari）</div>
                                        <ol className="list-decimal pl-4 space-y-1">
                                            <li>Safariでサイトを開く</li>
                                            <li>共有ボタン「□↑」→「ホーム画面に追加」</li>
                                            <li>名前を確認して追加</li>
                                        </ol>
                                        <div className="mt-3 rounded-md border p-2 text-[11px] bg-white/70" style={{ borderColor: theme.colors.border }}>
                                            <div className="flex items-center justify-between">
                                                <span className="opacity-70">インストール状況</span>
                                                <span className="font-semibold">{iosInstallStatusLabel}</span>
                                            </div>
                                            <div className="mt-2 space-y-2">
                                                <Button
                                                    size="sm"
                                                    onClick={handleOpenIosShare}
                                                    disabled={!isIosDevice}
                                                    className="w-full whitespace-normal break-words text-[11px] leading-tight h-auto py-2"
                                                    style={{ backgroundColor: theme.colors.primary, color: pickTextColor(theme.colors.primary) }}
                                                >
                                                    共有メニューを開く
                                                </Button>
                                                <div className="text-[10px] opacity-70">
                                                    共有メニューから「ホーム画面に追加」を選択してください。
                                                </div>
                                                {iosInstallHint ? (
                                                    <div className="text-[10px] text-amber-700">{iosInstallHint}</div>
                                                ) : null}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <Button
                                onClick={() => setShowNotifyGuide((prev) => !prev)}
                                className="w-full justify-between"
                                style={{ backgroundColor: theme.colors.accent, color: '#fff' }}
                            >
                                <span>通知許可の設定（Android / iOS）</span>
                                <span className="text-xs">{showNotifyGuide ? '閉じる' : '開く'}</span>
                            </Button>
                            {showNotifyGuide && (
                                <div className="grid gap-3 sm:grid-cols-2 text-xs">
                                    <div className="rounded-lg border p-3 bg-white/70" style={{ borderColor: theme.colors.border }}>
                                        <div className="font-semibold mb-2">Android（Chrome）</div>
                                        <ol className="list-decimal pl-4 space-y-1">
                                            <li>初回ログイン時の通知ダイアログで「許可」</li>
                                            <li>拒否してしまったら、鍵アイコン→サイト設定→通知→許可</li>
                                        </ol>
                                    </div>
                                    <div className="rounded-lg border p-3 bg-white/70" style={{ borderColor: theme.colors.border }}>
                                        <div className="font-semibold mb-2">iOS（Safari）</div>
                                        <ol className="list-decimal pl-4 space-y-1">
                                            <li>まずホーム画面に追加して、追加したアイコンから起動</li>
                                            <li>通知の確認が出たら「許可」</li>
                                            <li>拒否した場合は 設定→通知→Maison Portal を許可</li>
                                        </ol>
                                    </div>
                                </div>
                            )}
                            {showNotifyGuide && (
                                <div className="mt-3 rounded-md border p-3 text-[11px] bg-white/70" style={{ borderColor: theme.colors.border }}>
                                    <div className="flex items-center justify-between">
                                        <span className="opacity-70">通知の状態</span>
                                        <span className="font-semibold">{pushPermissionLabel}</span>
                                    </div>
                                    <div className="mt-2 space-y-2">
                                        <Button
                                            size="sm"
                                            onClick={handleEnablePush}
                                            disabled={isPushBusy || pushPermission === 'granted' || (isIosDevice && !isStandalone)}
                                            className="w-full"
                                            style={{ backgroundColor: theme.colors.accent, color: '#fff' }}
                                        >
                                            {pushPermission === 'granted' ? '通知は許可済み' : (isPushBusy ? '設定中...' : '通知を許可する')}
                                        </Button>
                                        <div className="text-[10px] opacity-70">
                                            {isIosDevice
                                                ? 'iOSはホーム画面に追加したアプリから起動した場合のみ通知が使えます。'
                                                : 'AndroidはChromeで通知を許可してください。'}
                                        </div>
                                        {pushNotifyHint ? (
                                            <div className="text-[10px] text-amber-700">{pushNotifyHint}</div>
                                        ) : null}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div
                        className="p-6 rounded-xl shadow-sm border backdrop-blur"
                        style={{
                            backgroundColor: `${theme.colors.card}E6`,
                            borderColor: theme.colors.border,
                            color: theme.colors.text
                        }}
                    >
                        <h3 className="font-semibold mb-6 border-b pb-2" style={{ borderColor: theme.colors.border }}>Resident Profile</h3>

                        <div className="flex flex-col items-center mb-6">
                            <Avatar
                                username={user.username}
                                src={user.avatar_path}
                                size="2xl"
                                theme={theme}
                                className="mb-4"
                            />

                            <label className="cursor-pointer text-sm font-medium hover:underline opacity-70 hover:opacity-100 transition-opacity">
                                <span style={{ color: theme.colors.primary }}>アイコンを変更する</span>
                                <input
                                    type="file"
                                    className="hidden"
                                    accept="image/*"
                                    onChange={(e) => {
                                        if (e.target.files && e.target.files[0]) {
                                            const file = e.target.files[0];
                                            const reader = new FileReader();
                                            reader.onload = () => {
                                                setSelectedImage(reader.result as string);
                                            };
                                            reader.readAsDataURL(file);
                                        }
                                    }}
                                />
                            </label>
                        </div>

                        <div className="space-y-3 text-sm">
                            <div className="flex justify-between items-center">
                                <span className="opacity-60">Display Name</span>
                                <div className="text-right">
                                    <span className="font-bold block">{user.display_name || '未設定'}</span>
                                    <Link to="/settings" className="text-xs hover:underline opacity-70" style={{ color: theme.colors.primary }}>
                                        表示名を変更する
                                    </Link>
                                </div>
                            </div>
                            <div className="flex justify-between">
                                <span className="opacity-60">Username</span>
                                <span className="font-medium opacity-80">{user.username}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="opacity-60">RoomNo.</span>
                                <div className="text-right">
                                    <span className="font-medium opacity-80 block">RoomNo. {user.room_no || '-'}</span>
                                    <span className="text-xs opacity-60">（隣人申請に使います）</span>
                                </div>
                            </div>
                            <div className="flex justify-between">
                                <span className="opacity-60">Email</span>
                                <span className="font-medium truncate max-w-[150px]">{user.email}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="opacity-60">Status</span>
                                <span className="text-green-600 font-medium">Active</span>
                            </div>
                        </div>
                    </div>

                    <div
                        className="p-6 rounded-xl shadow-sm border backdrop-blur"
                        style={{
                            backgroundColor: `${theme.colors.card}E6`,
                            borderColor: theme.colors.border,
                            color: theme.colors.text
                        }}
                    >
                        <h3 className="font-semibold mb-4 border-b pb-2" style={{ borderColor: theme.colors.border }}>System Announcement</h3>
                        <div className="text-sm opacity-80 leading-relaxed space-y-4">
                            {!['Admin', 'Maestro', 'Manager'].includes(user.role_type) ? (
                                <>
                                    <p>
                                        Maison Portalへようこそ。<br />
                                        現在、<span className="font-bold">第{user.floor_level}フロア：{theme.name}</span> のエリア権限が付与されています。<br />
                                        {user.floor_level !== 10 && 'より高い階層へ進むため、良質なコミュニケーションを心がけてください。'}
                                    </p>

                                    {theme.features && (
                                        <>
                                            <div className="bg-white/50 rounded-lg text-xs border overflow-hidden" style={{ borderColor: theme.colors.border }}>
                                                <div className="space-y-2 p-2">
                                                    <div className="flex gap-3 rounded-md bg-green-50/50 p-2">
                                                        <div className="text-lg">🔓</div>
                                                        <div>
                                                            <div className="font-bold text-green-800">解放済 (Current)</div>
                                                            <div className="text-green-900 leading-snug">{theme.features.current}</div>
                                                        </div>
                                                    </div>
                                                    <div className="flex gap-3 rounded-md bg-blue-50/50 p-2">
                                                        <div className="text-lg">🔮</div>
                                                        <div>
                                                            <div className="font-bold text-blue-800">次階層 (Next)</div>
                                                            <div className="text-blue-900 leading-snug">{theme.features.next}</div>
                                                        </div>
                                                    </div>
                                                    <div className="flex gap-3 rounded-md bg-red-50/50 p-2">
                                                        <div className="text-lg">⚠️</div>
                                                        <div>
                                                            <div className="font-bold text-red-800">降格 (Risk)</div>
                                                            <div className="text-red-900 leading-snug">{theme.features.demotion}</div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            {theme.features.details && (
                                                <div className="bg-white/60 rounded-lg text-xs border p-3 mt-3" style={{ borderColor: theme.colors.border }}>
                                                    <div className="font-bold mb-1">解放機能の詳細</div>
                                                    <div className="whitespace-pre-line leading-relaxed">{theme.features.details}</div>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </>
                            ) : (
                                <>
                                    <p>
                                        Maison Portalへようこそ。<br />
                                        現在、<span className="font-bold text-shadow-sm">{theme.name}</span> の権限が付与されています。<br />
                                        {user.role_type === 'Admin' && '管理業務、および居住者のサポートをお願いいたします。'}
                                    </p>
                                    {user.role_type === 'Admin' && (
                                        <div className="bg-black/50 rounded-lg text-xs border overflow-hidden p-3 mt-2" style={{ borderColor: theme.colors.border }}>
                                            <div className="font-bold mb-1 text-[#FFD700] drop-shadow-[0_1.2px_1.2px_rgba(0,0,0,0.8)]">Status:</div>
                                            <div className="text-white drop-shadow-[0_1.2px_1.2px_rgba(0,0,0,0.8)]">All Systems Operational.</div>
                                            <div className="opacity-70 mt-1 text-[#FFD700] drop-shadow-[0_1.2px_1.2px_rgba(0,0,0,0.8)]">Full Access Granted.</div>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Avatar Editor Modal */}
            {selectedImage && (
                <AvatarEditor
                    imageSrc={selectedImage}
                    onCancel={() => setSelectedImage(null)}
                    onSave={handleSaveAvatar}
                />
            )}

            {showCredentialModal && user?.must_change_credentials && (
                <div className="fixed inset-0 z-[200] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="w-full max-w-xl rounded-xl bg-white shadow-2xl border border-red-200">
                        <div className="px-6 py-5 border-b border-red-100 bg-red-50">
                            <h3 className="text-lg font-bold text-red-800">認証情報の更新が必要です</h3>
                            <p className="text-sm text-red-700 mt-2">
                                未変更の{credentialTargetLabel || '認証情報'}を設定しないと先へ進めません。忘れない情報へ今すぐ変更してください。
                            </p>
                            <p className="text-xs text-red-700 mt-2">
                                この設定だけは認証メールなしで即時反映されます。必ず記録し、絶対に忘れないようにしてください。
                            </p>
                        </div>

                        <form className="p-6 space-y-4" onSubmit={handleCredentialSubmit}>
                            {needsEmailCredentialUpdate && (
                                <>
                                    <div className="space-y-2">
                                        <label className="text-sm font-semibold text-gray-700">新しいメールアドレス</label>
                                        <input
                                            value={newEmail}
                                            onChange={(e) => setNewEmail(e.target.value)}
                                            onPaste={blockCredentialClipboard}
                                            onCopy={blockCredentialClipboard}
                                            onCut={blockCredentialClipboard}
                                            onDrop={blockCredentialClipboard}
                                            type="email"
                                            autoComplete="off"
                                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#800020]"
                                            placeholder="example@domain.com"
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-sm font-semibold text-gray-700">メールアドレス（確認）</label>
                                        <input
                                            value={confirmEmail}
                                            onChange={(e) => setConfirmEmail(e.target.value)}
                                            onPaste={blockCredentialClipboard}
                                            onCopy={blockCredentialClipboard}
                                            onCut={blockCredentialClipboard}
                                            onDrop={blockCredentialClipboard}
                                            type="email"
                                            autoComplete="off"
                                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#800020]"
                                            placeholder="もう一度入力してください"
                                        />
                                    </div>
                                </>
                            )}

                            {needsPasswordCredentialUpdate && (
                                <>
                                    <div className="space-y-2">
                                        <label className="text-sm font-semibold text-gray-700">新しいパスワード</label>
                                        <input
                                            value={newPassword}
                                            onChange={(e) => setNewPassword(e.target.value)}
                                            onPaste={blockCredentialClipboard}
                                            onCopy={blockCredentialClipboard}
                                            onCut={blockCredentialClipboard}
                                            onDrop={blockCredentialClipboard}
                                            type="password"
                                            autoComplete="new-password"
                                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#800020]"
                                            placeholder="8文字以上"
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-sm font-semibold text-gray-700">パスワード（確認）</label>
                                        <input
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            onPaste={blockCredentialClipboard}
                                            onCopy={blockCredentialClipboard}
                                            onCut={blockCredentialClipboard}
                                            onDrop={blockCredentialClipboard}
                                            type="password"
                                            autoComplete="new-password"
                                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#800020]"
                                            placeholder="もう一度入力してください"
                                        />
                                    </div>
                                </>
                            )}

                            {credentialError && (
                                <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                                    {credentialError}
                                </div>
                            )}

                            <Button
                                type="submit"
                                disabled={credentialSubmitting}
                                className="w-full mt-2"
                                style={{ backgroundColor: '#800020', color: '#fff' }}
                            >
                                {credentialSubmitting
                                    ? '更新中...'
                                    : needsEmailCredentialUpdate && needsPasswordCredentialUpdate
                                        ? 'メールアドレスとパスワードを更新する'
                                        : needsEmailCredentialUpdate
                                            ? 'メールアドレスを更新する'
                                            : 'パスワードを更新する'}
                            </Button>
                        </form>
                    </div>
                </div>
            )}
        </div >
    );
}
